import { Ellipsis, EllipsisVertical } from "lucide-react";
import { MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { IgIconButton, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  GetScriptStatusResponseBodyType,
  OrganizationDto,
  ScheduleDto,
  TabEnumFe,
  VariationFe,
} from "@intelligems/ig-types";
import { Menu, MenuItem } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useDuplicateExperience } from "@src/api/experiences/duplicate-experience";
import { usePatchExperience } from "@src/api/experiences/patch-experience";
import { processesKeys } from "@src/api/queryKeys";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { ScopedCreateExperienceWithDisabled } from "@src/components/LimitAccess/ScopedCreateExperienceWithDisabled";
import { ScopedStartExperienceWithDisabled } from "@src/components/LimitAccess/ScopedStartExperienceWithDisabled";
import { useIsUnderOfferLimit } from "@src/features/sharedExperience/hooks/use-is-under-offer-limit";
import { useDialogs } from "@src/hooks/dialog";
import { useIsExperienceMutating } from "@src/hooks/experiences";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { openDrawer } from "@src/store/slices/drawers";
import { Experience } from "@src/store/slices/experiences/types";
import { FoundExperimentDetails } from "@src/types/experience";
import { generatePreviewLink } from "@src/utils/generatePreviewLink";
import {
  mpExperienceCreated,
  mpExperienceEditStarted,
  mpExperienceGenericAction,
} from "@src/utils/mixpanel";
import { calculateProductCategories } from "@src/utils/mpUtils";
import { useQueryClient } from "@tanstack/react-query";

export interface MoreMenuPersonalizationProps {
  isUsedInTest: FoundExperimentDetails;
  experience: Experience;
  variations: (ExperienceListVariationDto | VariationFe)[];
  organization: OrganizationDto;
  renderLocation: "overview" | "experience" | "offers";
  onArchive?: () => void;
  onUnarchive?: () => void;
  schedules: ScheduleDto[];
  isSchedulesLoading: boolean;
  scriptStatus: GetScriptStatusResponseBodyType | null;
}

export const MoreMenuPersonalization = ({
  isUsedInTest,
  experience,
  organization,
  variations,
  renderLocation,
  schedules,
  isSchedulesLoading,
  onArchive,
  onUnarchive,
  scriptStatus,
}: MoreMenuPersonalizationProps) => {
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);
  const { openDialog } = useDialogs();

  const navigate = useNavigate();
  const patchExperience = usePatchExperience(experience?.id);
  const duplicateExperience = useDuplicateExperience("personalization");
  // The dialog starts the delete with its own useDeleteExperience(), so this
  // menu's isPending would never change. This hook finds that delete instead.
  const isDeleting = useIsExperienceMutating(
    "deleteExperience",
    experience.id ?? ""
  );
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useImmer<null | HTMLElement>(null);

  const open = Boolean(anchorEl);
  const enableScheduleTests = useConfiguration<boolean>(
    "enable_schedule_tests"
  )!.value;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDeleteExperience = () => {
    openDialog("confirmExperienceDeletion", {
      experience,
      category: "personalization",
      redirect: false,
      usedInTest: isUsedInTest,
    });
    handleClose();
  };

  const handleArchive = () => {
    if (onArchive) {
      onArchive();
    }
    if (experience.id) {
      openDialog("archivePersonalization", {
        experienceId: experience.id,
        shouldNavigate: renderLocation !== "offers",
        repopulate: true,
      });
    }
    handleClose();
  };

  const isUnderOfferLimit = useIsUnderOfferLimit();
  const handleUnarchive = () => {
    if (!isUnderOfferLimit) {
      openDialog("offerLimitReached");
      handleClose();
      return;
    }

    if (onUnarchive) {
      onUnarchive();
    }
    if (experience.id) {
      openDialog("unarchivePersonalization", {
        experienceId: experience.id,
        shouldNavigate: renderLocation !== "offers",
      });
    }
    handleClose();
  };

  const handleViewInfo = () => {
    dispatch(
      openDrawer({
        type: "experienceInfo",
        experience,
        isUsedInTest,
      })
    );
    handleClose();
  };

  return (
    <>
      <IgIconButton
        size={"small"}
        id="personalization-more-button"
        aria-controls="more-positioned-menu"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        loading={
          patchExperience.isPending ||
          duplicateExperience.isPending ||
          isDeleting > 0
        }
      >
        {renderLocation === "experience" ? (
          <EllipsisVertical size={20} />
        ) : (
          <Ellipsis size={20} />
        )}
      </IgIconButton>
      <Menu
        id="more-positioned-menu"
        aria-labelledby="more-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {(renderLocation === "overview" || renderLocation === "offers") && (
          <Link
            style={{ textDecoration: "none" }}
            to={`${PERSONALIZATION_ROUTES.Personalization}/${experience?.id}?action=edit`}
            state={{
              isUsedInActiveTest: isUsedInTest,
              discountId: variations[0]?.offerId,
            }}
            onClick={() => {
              mpExperienceEditStarted(experience);
              mpExperienceGenericAction(
                experience,
                "viewed",
                "more_menu_personalization"
              );
            }}
          >
            <MenuItem>
              <IgTypography>
                {experience.archivedAtTs ? "View" : "View and edit"}
              </IgTypography>
            </MenuItem>
          </Link>
        )}
        {experience.id && !experience.archivedAtTs && (
          <ScopedStartExperienceWithDisabled
            experience={experience}
            debugId={"schedule-personalization"}
            customDisabledTooltipMessage={UpgradePlanText}
            additionalEnableRequirements={[enableScheduleTests]}
          >
            <MenuItem
              disabled={!enableScheduleTests || isSchedulesLoading}
              onClick={() => {
                handleClose();
                openDialog("schedulePersonalization", {
                  experience,
                  schedules,
                });
              }}
            >
              <IgTypography>
                {schedules?.length
                  ? "Edit or cancel schedule"
                  : `Schedule ${renderLocation === "offers" ? "offer" : "personalization"}`}
              </IgTypography>
            </MenuItem>
          </ScopedStartExperienceWithDisabled>
        )}
        {experience.id !== undefined && (
          <MenuItem onClick={handleViewInfo}>
            <IgTypography>Show info</IgTypography>
          </MenuItem>
        )}
        {experience.id !== undefined && (
          <MenuItem
            onClick={() => {
              if (!scriptStatus?.exists) {
                openDialog("redirectToPlugin", {
                  id: experience.id!,
                  experience,
                  path: generatePreviewLink({ organization, experience }),
                });
              } else {
                window.open(
                  generatePreviewLink({ organization, experience }),
                  "_blank"
                );
              }
              handleClose();
            }}
          >
            <IgTypography>Preview</IgTypography>
          </MenuItem>
        )}
        {!experience.archivedAtTs && !!experience?.id ? (
          <ScopedCreateExperienceWithDisabled
            experience={experience}
            debugId={"duplicate-personalization"}
            customDisabledTooltipMessage={UpgradePlanText}
          >
            <MenuItem
              disabled={!experience?.id}
              style={{ textDecoration: "none", color: "black" }}
              onClick={() => {
                handleClose();
                queryClient.setQueryData(processesKeys.one(organization.id), {
                  processes: [
                    {
                      id: v4(),
                      orgId: organization.id,
                      type: "duplicateExperience",
                      severity: "info",
                      title: `Duplicating ${experience?.name}`,
                      isLoading: true,
                    },
                  ],
                });
                if (experience.id) {
                  duplicateExperience.mutate({
                    body: undefined,
                    pathParams: { experienceId: experience.id },
                    queryParams: undefined,
                  });
                }
                mpExperienceCreated(
                  "personalization",
                  renderLocation,
                  calculateProductCategories(experience),
                  true,
                  experience.id
                );
              }}
            >
              <IgTypography>Duplicate</IgTypography>
            </MenuItem>
          </ScopedCreateExperienceWithDisabled>
        ) : null}
        {isUsedInTest.id && (
          <MenuItem
            onClick={() => {
              void navigate(
                `${EXPERIMENT_ROUTES.Experiment}/${isUsedInTest.id}?action=edit&tab=${TabEnumFe.MODS}&type=personalization/offer`
              );
              handleClose();
            }}
          >
            <IgTypography>View test</IgTypography>
          </MenuItem>
        )}
        {experience.id !== undefined && (
          <MenuItem
            onClick={() => {
              dispatch(
                openDrawer({
                  type: "history",
                  experienceType: "personalization/offer",
                  experienceInfo:
                    renderLocation === "overview" || renderLocation === "offers"
                      ? experience
                      : undefined,
                })
              );
              handleClose();
            }}
          >
            <IgTypography>View history</IgTypography>
          </MenuItem>
        )}
        {!experience.archivedAtTs ? (
          experience.status !== "started" && (
            <MenuItem disabled={!experience?.id} onClick={handleArchive}>
              <IgTypography>Archive</IgTypography>
            </MenuItem>
          )
        ) : (
          <MenuItem disabled={!experience?.id} onClick={handleUnarchive}>
            <IgTypography>Unarchive</IgTypography>
          </MenuItem>
        )}
        {(renderLocation === "overview" || renderLocation === "offers") &&
          experience.id !== undefined &&
          experience.status !== "started" &&
          !schedules.length && (
            <MenuItem onClick={onDeleteExperience}>
              <IgTypography>Delete</IgTypography>
            </MenuItem>
          )}
        {renderLocation === "experience" &&
          searchParams.get("tab") === TabEnumFe.ANALYTICS &&
          experience.status !== "pending" && (
            <MenuItem
              onClick={() => {
                openDialog("dataExport");
                handleClose();
              }}
            >
              <IgTypography>Export data</IgTypography>
            </MenuItem>
          )}
      </Menu>
    </>
  );
};
